/**
 * 블로그 목 조회 — 서버가 `lang` 을 받아 답하는 방식을 흉내 낸다.
 * @description 번역이 있는 글은 그 언어로, 없는 글은 원문(`EN`)으로 싣는다. 번역 없는 글도
 *   목록에서 빠지지 않는다 — 서버와 같다.
 *
 *   스페인어 번역은 첫 글 하나에만 둔다. `/es/blog` 의 두 갈래(번역 있음 → 그린다, 없음 →
 *   영어 주소로 보낸다)를 로컬에서 둘 다 밟을 수 있게.
 */
import { pageOf } from "./paging";
import { MOCK_BLOG_ITEMS } from "./posts";
import type { BlogDetail, BlogListResponse } from "@/domains/core/post";
import {
  BLOG_LANGUAGES,
  ORIGINAL_BLOG_LANGUAGE,
  type BlogLanguage,
} from "@/shared/config";
import { ApiError } from "@/shared/lib/http";

type Translation = Pick<BlogDetail, "title" | "excerpt" | "content">;

const NOT_FOUND = 404;

const TRANSLATIONS: Partial<Record<BlogLanguage, Record<string, Translation>>> =
  {
    es: {
      "creator-desk-setup-for-cover-video-editing": {
        title: "Un escritorio de creador para editar vídeos de covers",
        excerpt:
          "Cómo montamos el escritorio para editar covers en un fin de semana.",
        content:
          "Todo empezó en un chat a las dos de la madrugada.\n\n## Qué queríamos hacer\n\nAlgo que un fan pudiera tener en la mano, no una captura de pantalla.\n\n- **Un fin de semana**, de principio a fin\n- Cada persona aporta lo que sabe hacer\n\n> «Si tarda más de un fin de semana, es un proyecto. Si tarda un fin de semana, es una fiesta.»",
      },
    },
  };

const code = (language: BlogLanguage) => language.toUpperCase();

function inLanguage(post: BlogDetail, language: BlogLanguage): BlogDetail {
  const translation = TRANSLATIONS[language]?.[post.slug];
  const available = BLOG_LANGUAGES.filter(
    (lang) =>
      lang === ORIGINAL_BLOG_LANGUAGE || TRANSLATIONS[lang]?.[post.slug],
  ).map(code);
  return {
    ...post,
    ...translation,
    language: code(translation ? language : ORIGINAL_BLOG_LANGUAGE),
    availableLanguages: available,
  };
}

export function mockBlogDetail(
  slug: string,
  language: BlogLanguage,
): Promise<BlogDetail> {
  const post = MOCK_BLOG_ITEMS.find((it) => it.slug === slug);
  return post
    ? Promise.resolve(inLanguage(post, language))
    : Promise.reject(new ApiError(NOT_FOUND, `getBlogDetail(${slug}): 404`));
}

export function mockBlogItems(
  limit: number,
  cursor: string | undefined,
  language: BlogLanguage,
): Promise<BlogListResponse> {
  const { data, nextCursor } = pageOf(MOCK_BLOG_ITEMS, cursor, limit);
  return Promise.resolve({
    data: data.map((post) => inLanguage(post, language)),
    nextCursor,
  });
}
